/**
 * External Imports
 */
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CSVLink } from 'react-csv';
import { sub } from 'date-fns';

/**
 * Nexus Imports
 */
import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

/**
 * Internal Imports
 */
import { RootState } from '../../../../redux/store';
import {
  clearCsvToExport,
  getStoplightsByLocationDownload,
  getIssuesByUnitIdsOrEmployeeListDownload,
  getIssueReportByFacilityIdDownload,
  getFeedbackByUnitIdsOrEmployeeListDownload,
  getFeedbackReportByFacilityIdDownload
} from '../../../../redux/actions';

import { logTaskClick } from '../../../../utils/analyticsHelpers';
import { date } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  Employee,
  Filter,
  GetTaskParams,
  Unit
} from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  StyledDownloadBtn
  // TinySpinner
} from './DownloadBtn.styles';

interface DownloadCSVProps {
  authorizedUser: AuthUserProps;
  dataToCsvExport?: any[];
  employeesToRound: Employee[];
  isLoading?: boolean;
  issueFilter: Filter;
  feedbackFilter: Filter;
  fileNamePrefix: string;
  location: string;
  selectedDelegate: string;
  selectedUnits: { id: string; unit: string }[];
  sortDirStoplight: string;
  sortKeyStoplight: string;
  sortDirTask: string;
  sortKeyTask: string;
  stoplightFilter: Filter;
  stoplightSearchQuery: string;
  taskSearchQuery: string;
  units: Unit[];
  downloadStoplight?: () => void;
}

const DownloadBtn: FC<DownloadCSVProps> = ({
  authorizedUser,
  dataToCsvExport,
  employeesToRound,
  isLoading,
  issueFilter,
  feedbackFilter,
  fileNamePrefix,
  location,
  selectedDelegate,
  selectedUnits,
  sortDirStoplight,
  sortKeyStoplight,
  sortDirTask,
  sortKeyTask,
  stoplightFilter,
  stoplightSearchQuery,
  taskSearchQuery,
  units,
  downloadStoplight
}) => {
  // TODO: This whole component needs to be refactored
  const dispatch = useDispatch();

  const [csvData, setCsvData] = useState<any[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<
    { label: string; key: string }[] | string[]
  >([]);

  const [csvFileName, setCsvFileName] = useState<string>('');

  const { logTrackingEvent } = useAnalyticsApi();
  const { '*': view } = useParams();

  const csvLinkRef = useRef<
    (CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }) | null
  >(null);
  const firstUpdate: { current: boolean } = useRef(true); // to detect first render

  const selectedUnitIds = useMemo(
    () => selectedUnits.map(item => item.id),
    [selectedUnits]
  );

  const downloadAllIssues = useCallback(() => {
    const filterData = {
      sortBy: sortKeyTask,
      sortOrder: sortDirTask,
      query: taskSearchQuery
    } as GetTaskParams;
    if (location === 'patient') {
      filterData.facilityId = authorizedUser.facilityId;
      filterData.units = selectedUnits.map(val => val.id).join(',');
    } else {
      filterData.employeeIds = employeesToRound
        .map((val: Employee) => val.hcaid)
        .join(',');
    }

    if (Object.keys(issueFilter).length > 0) {
      const {
        startDate,
        endDate,
        category,
        subCategory,
        createdBy,
        employeeId,
        complete,
        opened,
        attachedToStoplight,
        hasUpdate,
        patient
      } = issueFilter;
      const statuses = [];
      if (complete) {
        statuses.push('completed');
      }
      if (opened) {
        statuses.push('opened');
      }
      if (attachedToStoplight) {
        filterData.attachedToStoplight = attachedToStoplight;
      }
      if (hasUpdate) {
        filterData.hasComment = hasUpdate;
      }
      filterData.startDate = startDate
        ? date.formatFilterStartDate(startDate)
        : date.formatFilterStartDate(
            date.convertToDayOfYear(sub(new Date(), { days: 30 }))
          );
      filterData.endDate = endDate
        ? date.formatFilterEndDate(endDate)
        : date.formatFilterEndDate(date.convertToDayOfYear(new Date()));

      if (location === 'patient') {
        filterData.category = category;
        filterData.subCategory = subCategory;
        filterData.createdBy = createdBy;
        filterData.patientName = patient;
        filterData.status = statuses;

        return dispatch(
          getIssuesByUnitIdsOrEmployeeListDownload({
            location,
            filterData
          })
        );
      }
      if (selectedDelegate === authorizedUser.facilityId) {
        delete filterData.employeeIds;
        return dispatch(
          getIssueReportByFacilityIdDownload({
            location,
            filterData: {
              ...filterData,
              facilityId: authorizedUser.facilityId,
              category,
              subCategory,
              createdBy,
              employeeId,
              status: statuses
            },
            roundingTypes: ['employee', 'cned', 'csrn']
          })
        );
      }
      filterData.category = category;
      filterData.subCategory = subCategory;
      filterData.createdBy = createdBy;
      filterData.employeeId = employeeId;
      filterData.status = statuses;
      filterData.userId = selectedDelegate || authorizedUser.hcaid;
      return dispatch(
        getIssuesByUnitIdsOrEmployeeListDownload({
          location,
          filterData
        })
      );
    }

    filterData.startDate = date.formatFilterStartDate(
      date.convertToDayOfYear(sub(new Date(), { days: 30 }))
    );

    if (location === 'patient') {
      return dispatch(
        getIssuesByUnitIdsOrEmployeeListDownload({
          location,
          filterData: {
            ...filterData,
            facilityId: authorizedUser.facilityId,
            units: selectedUnits.map(val => val.id).join(',')
          }
        })
      );
    }
    if (selectedDelegate === authorizedUser.facilityId) {
      delete filterData.employeeIds;
      return dispatch(
        getIssueReportByFacilityIdDownload({
          location,
          filterData: {
            ...filterData,
            facilityId: authorizedUser.facilityId
          },
          roundingTypes: ['employee', 'cned', 'csrn']
        })
      );
    }
    return dispatch(
      getIssuesByUnitIdsOrEmployeeListDownload({
        location,
        filterData: {
          ...filterData,
          userId: selectedDelegate || authorizedUser.hcaid,
          employeeIds: employeesToRound
            .map((val: Employee) => val.hcaid)
            .join(',')
        }
      })
    );
  }, [
    authorizedUser,
    location,
    selectedDelegate,
    issueFilter,
    sortDirTask,
    sortKeyTask,
    taskSearchQuery,
    selectedUnitIds
  ]);

  const downloadAllFeedback = useCallback(() => {
    let filterData: any = {
      sortBy: sortKeyTask,
      sortOrder: sortDirTask,
      startDate: date.formatFilterStartDate(
        date.convertToDayOfYear(sub(new Date(), { days: 30 }))
      ),
      query: taskSearchQuery
    };
    if (location === 'patient') {
      const selectedUnitsIds = selectedUnits.map(val => val.id);
      filterData.facilityId = authorizedUser.facilityId;
      filterData.units = selectedUnitsIds.join(',');
      filterData.employeeId = '';
    } else {
      const hcaIds = employeesToRound.map((val: Employee) => val.hcaid);
      filterData.employeeIds = hcaIds.join(',');
      filterData.searchEmployeeId = '';
    }
    if (Object.keys(feedbackFilter).length > 0) {
      const {
        startDate,
        endDate,
        category,
        subCategory,
        createdBy,
        patient,
        employeeId,
        complete,
        opened
      } = feedbackFilter;
      const statuses = [];
      if (complete) {
        statuses.push('completed');
      }
      if (opened) {
        statuses.push('opened');
      }

      if (location === 'patient') {
        filterData.startDate = startDate
          ? date.formatFilterStartDate(startDate)
          : date.formatFilterStartDate(
              date.convertToDayOfYear(sub(new Date(), { days: 30 }))
            );
        filterData.endDate = endDate
          ? date.formatFilterEndDate(endDate)
          : date.formatFilterEndDate(date.convertToDayOfYear(new Date()));
        filterData.category = category;
        filterData.subCategory = subCategory;
        filterData.createdBy = createdBy;
        filterData.patientName = patient;
        filterData.searchEmployeeId = employeeId;
        filterData.status = statuses;
      } else {
        if (selectedDelegate === authorizedUser.facilityId) {
          delete filterData.employeeIds;
          return dispatch(
            getFeedbackReportByFacilityIdDownload({
              location,
              filterData: {
                ...filterData,
                facilityId: authorizedUser.facilityId,
                startDate: startDate
                  ? date.formatFilterStartDate(startDate)
                  : date.formatFilterStartDate(
                      date.convertToDayOfYear(sub(new Date(), { days: 30 }))
                    ),
                endDate: endDate
                  ? date.formatFilterEndDate(endDate)
                  : date.formatFilterEndDate(
                      date.convertToDayOfYear(new Date())
                    ),
                category,
                subCategory,
                createdBy,
                searchEmployeeId: employeeId,
                status: statuses
              },
              roundingTypes: ['employee', 'cned', 'csrn']
            })
          );
        }
        filterData = {
          ...filterData,
          startDate: startDate
            ? date.formatFilterStartDate(startDate)
            : date.formatFilterStartDate(
                date.convertToDayOfYear(sub(new Date(), { days: 30 }))
              ),
          endDate: endDate
            ? date.formatFilterEndDate(endDate)
            : date.formatFilterEndDate(date.convertToDayOfYear(new Date())),
          category,
          subCategory,
          createdBy,
          searchEmployeeId: employeeId.length > 0 ? employeeId : '',
          status: statuses,
          userId: selectedDelegate || authorizedUser.hcaid
        };
      }
      return dispatch(
        getFeedbackByUnitIdsOrEmployeeListDownload({
          location,
          filterData
        })
      );
    }
    if (location === 'patient') {
      const selectedUnitsIds = selectedUnits.map(val => val.id);
      return dispatch(
        getFeedbackByUnitIdsOrEmployeeListDownload({
          location,
          filterData: {
            ...filterData,
            units: selectedUnitsIds.join(',')
          }
        })
      );
    }
    // For feedback list based on facility
    const filterObj = {
      ...filterData,
      facilityId: selectedDelegate
    };
    if (selectedDelegate === authorizedUser.facilityId) {
      delete filterObj.employeeIds;
      return dispatch(
        getFeedbackReportByFacilityIdDownload({
          location,
          filterData: filterObj,
          roundingTypes: ['employee', 'cned', 'csrn']
        })
      );
    }
    return dispatch(
      getFeedbackByUnitIdsOrEmployeeListDownload({
        location,
        filterData: {
          ...filterData,
          userId: selectedDelegate || authorizedUser.hcaid,
          employeeIds: employeesToRound.map((item: Employee) => item.hcaid)
        }
      })
    );
  }, [
    authorizedUser,
    location,
    selectedDelegate,
    feedbackFilter,
    sortDirTask,
    sortKeyTask,
    taskSearchQuery,
    selectedUnitIds
  ]);

  const downloadAllStoplights = useCallback(() => {
    if (Object.keys(stoplightFilter).length === 0) {
      if (location === 'patient') {
        dispatch(
          getStoplightsByLocationDownload({
            facilityId: authorizedUser.facilityId,
            locationIds: stoplightSearchQuery
              ? units.map(item => item.unitId)
              : selectedUnits.map(item => item.id),
            locationType: 'unit',
            origin: 'patient',
            sortBy: sortKeyStoplight,
            sortOrder: sortDirStoplight,
            query: stoplightSearchQuery || '',
            startDate: date.formatFilterStartDate(
              date.convertToDayOfYear(sub(new Date(), { days: 30 }))
            )
          })
        );
      } else {
        dispatch(
          getStoplightsByLocationDownload({
            locationIds: [authorizedUser.facilityId],
            locationType: 'facility',
            origin: 'employee',
            sortBy: sortKeyStoplight,
            sortOrder: sortDirStoplight,
            query: stoplightSearchQuery || '',
            startDate: date.formatFilterStartDate(
              date.convertToDayOfYear(sub(new Date(), { days: 30 }))
            )
          })
        );
      }
    } else {
      const {
        startDate,
        endDate,
        category,
        subCategory,
        createdBy,
        complete,
        opened,
        deferred,
        hasAttachedIssues,
        hasUpdate
      } = stoplightFilter;
      let statuses = [];
      if (complete) {
        statuses.push('completed');
      }
      if (opened) {
        statuses.push('inProgress');
      }
      if (deferred) {
        statuses.push('deferred');
      }
      if (!complete && !opened && !deferred) {
        statuses = ['completed', 'deferred', 'inProgress'];
      }

      if (location === 'patient') {
        dispatch(
          getStoplightsByLocationDownload({
            locationIds: selectedUnits.map(item => item.id),
            facilityId: authorizedUser.facilityId,
            locationType: 'unit',
            origin: 'patient',
            startDate: startDate
              ? date.formatFilterStartDate(startDate)
              : date.formatFilterStartDate(
                  date.convertToDayOfYear(sub(new Date(), { days: 30 }))
                ),
            endDate: endDate
              ? date.formatFilterEndDate(endDate)
              : date.formatFilterEndDate(date.convertToDayOfYear(new Date())),
            createdBy,
            category,
            subCategory,
            hasIssues: hasAttachedIssues,
            hasComment: hasUpdate,
            status: statuses,
            sortBy: sortKeyStoplight,
            sortOrder: sortDirStoplight,
            query: stoplightSearchQuery || ''
          })
        );
      } else {
        dispatch(
          getStoplightsByLocationDownload({
            locationIds: [authorizedUser.facilityId],
            locationType: 'facility',
            origin: 'employee',
            startDate: startDate
              ? date.formatFilterStartDate(startDate)
              : date.formatFilterStartDate(
                  date.convertToDayOfYear(sub(new Date(), { days: 30 }))
                ),
            endDate: endDate
              ? date.formatFilterEndDate(endDate)
              : date.formatFilterEndDate(date.convertToDayOfYear(new Date())),
            createdBy,
            category,
            subCategory,
            hasIssues: hasAttachedIssues,
            hasComment: hasUpdate,
            status: statuses,
            sortBy: sortKeyStoplight,
            sortOrder: sortDirStoplight,
            query: stoplightSearchQuery || ''
          })
        );
      }
    }
  }, [
    authorizedUser,
    location,
    sortKeyStoplight,
    sortDirStoplight,
    stoplightFilter,
    authorizedUser,
    stoplightSearchQuery,
    selectedUnits
  ]);

  const handleDownloadClick = useCallback(() => {
    if (
      view?.includes('issues') ||
      view?.includes('feedback') ||
      view?.includes('stoplight')
    ) {
      logTrackingEvent(logTaskClick(view, 'download'));
    }
    return view?.includes('stoplight')
      ? downloadAllStoplights()
      : view?.includes('issues')
      ? downloadAllIssues()
      : view?.includes('feedback')
      ? downloadAllFeedback()
      : downloadStoplight && downloadStoplight();
  }, [
    view,
    downloadAllFeedback,
    downloadAllIssues,
    downloadAllStoplights,
    downloadStoplight,
    logTrackingEvent,
    logTaskClick
  ]);

  // clear the data on first render
  useEffect(() => {
    dispatch(clearCsvToExport());
  }, []);

  useEffect(() => {
    // to make this useEffect not to run on first render
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    if (dataToCsvExport?.length) {
      let headersList: { label: string; key: string }[] | string[] =
        view?.includes('stoplight') ||
        view?.includes('issues') ||
        view?.includes('feedback')
          ? []
          : Object.keys(dataToCsvExport[0]);
      if (view?.includes('stoplight')) {
        headersList = [
          { label: 'Created Date', key: 'createdDate' },
          { label: 'Stoplight Id', key: 'friendlyId' },
          { label: 'Created By', key: 'createdBy' },
          { label: 'Facility', key: 'facility' },
          {
            label: location === 'patient' ? 'Unit' : 'Department',
            key: 'location'
          },
          { label: 'Category', key: 'category' },
          { label: 'Subcategory', key: 'subCategory' },
          { label: 'Description', key: 'details' },
          { label: 'Attached Issues', key: 'attachedIssues' },
          { label: 'Last Comment', key: 'latestComment' },
          { label: 'Status', key: 'status' }
        ];
      } else if (view?.includes('issues')) {
        // when view is issues
        headersList = [
          { label: 'Created Date', key: 'taskDate' },
          { label: 'Issue Id', key: 'friendlyId' },
          { label: 'Created by', key: 'userFullName' },
          { label: 'Facility', key: 'facility' },
          {
            label: location === 'patient' ? 'Unit' : 'Department',
            key: 'location'
          },
          { label: 'Category', key: 'category' },
          { label: 'Subcategory', key: 'subCategory' },
          { label: 'Description', key: 'description' },
          {
            label: location === 'patient' ? 'Patient' : 'Employee',
            key: 'name'
          },
          { label: 'Last Comment', key: 'latestComment' },
          { label: 'Attached To', key: 'attachedTo' },
          { label: 'Status', key: 'status' }
        ];
      } else if (view?.includes('feedback')) {
        // when view is feedback
        headersList = [
          { label: 'Created Date', key: 'taskDate' },
          { label: 'Created by', key: 'userFullName' },
          { label: 'Facility', key: 'facility' },
          {
            label: location === 'patient' ? 'Unit' : 'Department',
            key: 'location'
          },
          { label: 'Type', key: 'taskSubtype' },
          { label: 'Category', key: 'category' },
          { label: 'Subcategory', key: 'subCategory' },
          { label: 'Description', key: 'description' },
          {
            label: 'Employee Name',
            key: 'employeeName'
          },
          { label: 'Status', key: 'status' }
        ];
        if (location === 'patient') {
          headersList.splice(9, 0, {
            label: 'Patient Name',
            key: 'patientName'
          });
        }
      }
      setCsvHeaders(headersList);
      setCsvData(dataToCsvExport);

      // get current date to make it to the file name
      const today = new Date();
      const timestamp = today.toISOString().slice(0, 16).replace(/T/g, '_');
      const fileName = `${fileNamePrefix}-${timestamp}`;
      setCsvFileName(fileName);
    }
  }, [dataToCsvExport]);

  useEffect(() => {
    if (csvData?.length && csvHeaders?.length)
      csvLinkRef?.current?.link.click();
  }, [csvData]);

  return (
    <div>
      <StyledDownloadBtn
        id="Download-Button"
        className="w-100 me-1"
        color="blue"
        disabled={isLoading}
        icon="get_app"
        kind="secondary"
        size="small"
        style={{ fontSize: '1rem' }}
        onClick={handleDownloadClick}
      >
        {/* {isLoading ? (
          <TinySpinner
            kind="spinner"
            role="status"
            size="small"
            style={{ marginRight: '10px' }}
          />
        ) : (
          ''
        )} */}
        Download
      </StyledDownloadBtn>
      <CSVLink
        ref={csvLinkRef}
        data={csvData}
        headers={csvHeaders}
        filename={csvFileName}
        style={{ display: 'none' }}
      >
        DOWNLOAD
      </CSVLink>
    </div>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser?.authorizedUser,
    dataToCsvExport: state.AdminReducer.dataToCsvExport,
    employeesToRound: state.EmployeeReducer.employeesToRound,
    isLoading: state.AdminReducer.isLoadingCsvExport,
    issueFilter: state.TaskReducer.issueFilter,
    selectedDelegate: state.EmployeeReducer.selectedDelegatedEmployee.hcaid,
    stoplightFilter: state.StoplightReducer.stoplightFilter,
    sortDirTask: state.TaskReducer.sortDir,
    sortKeyTask: state.TaskReducer.sortKey,
    sortDirStoplight: state.StoplightReducer.sortDir,
    sortKeyStoplight: state.StoplightReducer.sortKey,
    location: state.UserReducer.userSection,
    selectedUnits: state.ConfigReducer.selectedUnits,
    feedbackFilter: state.TaskReducer.feedbackFilter,
    stoplightSearchQuery: state.StoplightReducer.stoplightSearchQuery,
    taskSearchQuery: state.TaskReducer.taskSearchQuery,
    units: state.ConfigReducer.units
  };
};

export default connect(mapReduxStateToProps)(DownloadBtn);
